import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { DayCellEventList } from './DayCellEventList';
const POPOVER_WIDTH = 240;
const MARGIN = 8;
/**
 * Absolutely positioned popover that shows all events for a single day.
 * Mounts only when opened (lazy), so event components are never rendered
 * for the full list until the user requests them (Phase 11 lazy-load alignment).
 *
 * Positioning: tries to open below the anchor cell; flips above if it would
 * overflow the viewport bottom.  Uses the popover's actual rendered height
 * (measured via useLayoutEffect) so the flip offset is accurate regardless
 * of how many events are displayed.
 */
export function MoreEventsPopover({ date, events, anchorRect, onClose, renderEvent }) {
    const popoverRef = useRef(null);
    // Position state — starts off-screen; useLayoutEffect repositions before paint.
    const [position, setPosition] = useState({ top: -9999, left: -9999 });
    // Measure actual popover height and compute position before the browser paints.
    useLayoutEffect(() => {
        if (!popoverRef.current)
            return;
        const viewportHeight = window.innerHeight;
        const popoverHeight = popoverRef.current.offsetHeight;
        const { bottom: anchorBottom, left: anchorLeft, top: anchorTop } = anchorRect;
        let top = anchorBottom + MARGIN;
        let left = anchorLeft;
        // Flip above if it would overflow bottom — use actual height, not max
        if (top + popoverHeight > viewportHeight - MARGIN) {
            top = anchorTop - popoverHeight - MARGIN;
        }
        // Clamp to right edge
        if (left + POPOVER_WIDTH > window.innerWidth - MARGIN) {
            left = window.innerWidth - POPOVER_WIDTH - MARGIN;
        }
        setPosition({ top, left });
    }, [anchorRect]);
    // Close on outside click or Escape key
    useEffect(() => {
        function handlePointerDown(e) {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                onClose();
            }
        }
        function handleKeyDown(e) {
            if (e.key === 'Escape')
                onClose();
        }
        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);
    const style = {
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: POPOVER_WIDTH,
        zIndex: 1000
    };
    return (React.createElement("div", { ref: popoverRef, className: "qmb-month-grid__popover", style: style, role: "dialog", "aria-modal": "false" },
        React.createElement("div", { className: "qmb-month-grid__popover-header" },
            React.createElement("span", { className: "qmb-month-grid__popover-date" }, dayjs(date).format('dddd, MMMM D')),
            React.createElement("button", { type: "button", className: "qmb-month-grid__popover-close", "aria-label": "Close", onClick: onClose },
                React.createElement("span", { className: "fa fa-times", "aria-hidden": "true" }))),
        React.createElement(DayCellEventList, { events: events, renderEvent: renderEvent })));
}
